import { Control, Controller, UseFormResetField, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { MultiSelectPanelHeaderTemplateParams } from 'primereact/multiselect'
import { useTranslation } from 'react-i18next'

// local imports
import DateUtil from 'utils/DateUtil'
import useGetLocationFilterLists from '../hooks/useGetLocationFilterLists'
import { IMapSearchForm } from '../PageWrapper'
import DatePickerIcon from 'components/ui/DatePickerIcon'
import { DropdownWrapper, MultiSelectWrapper, StyledCustomDropdown, StyledMultiSelect } from '../styled'
import useDidMountEffect from 'utils/useDidMountEffect'

interface ISearchFormProps {
  control: Control<IMapSearchForm>
  watch: UseFormWatch<IMapSearchForm>
  resetField: UseFormResetField<IMapSearchForm>
  setValue: UseFormSetValue<IMapSearchForm>
}

//  ['Şirketimin Ofisleri', 'Ortak Ofisler']}

function valueTemplate(options: any, props: any) {
  return <div className="selectedVal">{options?.name || props?.placeholder}</div>
}

/* #################### Search Form Component #################### */
export default function SearchForm({ control, resetField, watch, setValue }: ISearchFormProps) {
  const { t } = useTranslation()
  const country = watch('country')
  const city = watch('city')
  const startDate = watch('startDate')
  const endDate = watch('endDate')
  const resTypeOptions = [
    { name: t('tabs.company-work'), value: 'Şirketimin Ofisleri' },
    { name: t('tabs.shared-office'), value: 'Ortak Ofisler' },
  ]

  useDidMountEffect(() => {
    if (startDate > endDate) {
      setValue('endDate', startDate)
    }
  }, [startDate])

  useDidMountEffect(() => {
    if (endDate < startDate) {
      setValue('startDate', endDate)
    }
  }, [endDate])

  /* Location Dropdowns (Country-City-District) */
  const { cityList, countryList, districtList } = useGetLocationFilterLists(country?.id, city?.id)

  /* Templates etc. */
  function panelHeaderTemplate(options: MultiSelectPanelHeaderTemplateParams) {
    return (
      <div className="p-multiselect-header">
        <label className="d-flex align-items-center">
          {options.checkboxElement}
          <span role="button" className="px-2 user-select-none">
            {t('buttons.choose-all')}
          </span>
        </label>
        {options.closeElement}
      </div>
    )
  }

  return (
    <div className="d-flex flex-column gap-2" id="searchFormWrapper">
      {/* Country */}
      <Controller
        name="country"
        control={control}
        render={({ field }) => (
          <DropdownWrapper id="mapCountry">
            <label className="autocomplete-title">{t('input-texts.country')}</label>
            <StyledCustomDropdown
              showClear
              id="country"
              emptyMessage={t('most-used-words.option-not-found')}
              emptyFilterMessage={t('most-used-words.option-not-found')}
              name="country"
              placeholder={t('input-texts.choose-country')}
              optionLabel="name"
              valueTemplate={(opt, props) => valueTemplate(opt, props)}
              options={countryList}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.value)
                resetField('city')
                resetField('district')
              }}
            />
          </DropdownWrapper>
        )}
      />

      <Controller
        name="city"
        control={control}
        render={({ field }) => (
          <DropdownWrapper id="mapCity">
            <label className="autocomplete-title">{t('input-texts.city')}</label>
            <StyledCustomDropdown
              showClear
              disabled={!country}
              id="city"
              emptyMessage={t('most-used-words.option-not-found')}
              name="city"
              placeholder={t('input-texts.choose-city')}
              optionLabel="name"
              valueTemplate={(opt, props) => valueTemplate(opt, props)}
              options={country ? cityList : []}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.value)
                resetField('district')
              }}
            />
          </DropdownWrapper>
        )}
      />

      <Controller
        name="district"
        control={control}
        render={({ field }) => (
          <DropdownWrapper id="mapDistrict">
            <label className="autocomplete-title">{t('input-texts.district')}</label>
            <StyledCustomDropdown
              showClear
              disabled={!city}
              id="district"
              emptyMessage={t('most-used-words.option-not-found')}
              name="district"
              placeholder={t('input-texts.choose-district')}
              optionLabel="name"
              valueTemplate={(opt, props) => valueTemplate(opt, props)}
              options={city ? districtList : []}
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
            />
          </DropdownWrapper>
        )}
      />

      <Controller
        name="resType"
        control={control}
        render={({ field }) => (
          <MultiSelectWrapper id="mapResType">
            <label className="autocomplete-title">{t('input-texts.res-type')}</label>
            <StyledMultiSelect
              id="mapResType"
              name="resType"
              selectedItemsLabel={t('most-used-words.all-selected')}
              filter={false}
              scrollHeight="145px"
              maxSelectedLabels={2}
              panelHeaderTemplate={panelHeaderTemplate}
              options={resTypeOptions}
              optionLabel="name"
              optionValue="value"
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
              placeholder={t('input-texts.choose-res-type-map')}
            />
          </MultiSelectWrapper>
        )}
      />

      <div className="d-flex gap-3">
        <Controller
          name="startDate"
          control={control}
          render={({ field }) => (
            <DatePickerIcon
              onFocus={() => {}}
              touchUI
              selectOtherMonths
              showOtherMonths
              disabledDays={[]}
              idName="mapReservationStartDate"
              defaultDate={field.value}
              minDate={DateUtil.previousMonth()}
              maxDate={endDate || DateUtil.futureMonth()}
              onSelectedDateChange={(e) => field.onChange(e.value as Date)}
              label={t('input-texts.start-date')}
            />
          )}
        />

        <Controller
          name="endDate"
          control={control}
          render={({ field }) => (
            <DatePickerIcon
              onFocus={() => {}}
              touchUI
              selectOtherMonths
              showOtherMonths
              disabledDays={[]}
              idName="mapReservationEndDate"
              defaultDate={field.value}
              minDate={startDate || DateUtil.previousMonth()}
              maxDate={DateUtil.futureMonth()}
              onSelectedDateChange={(e) => field.onChange(e.value as Date)}
              label={t('input-texts.end-date')}
            />
          )}
        />
      </div>
    </div>
  )
}
